import React from 'react'
import {
    Stack,
    Box,
    Avatar,
    Typography,
    Paper,
    useMediaQuery,
    IconButton,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded'

import { useShoppingCart } from '../context/ShoppingCartContext'
import IncDec from './ui/incdec'
import { config } from '../utilities/constants'
import { getMoneyFormat, getLineItemTotal } from '../utilities/money'

import { Colors } from '../styles/theme'
import CorrectCopyright from './CorrectCopyright.js'

export function CartItem({ productIDNum, quantity, storeItems }) {
    const { incrItemQty, decrItemQty, removeFromCart, isCAD } =
        useShoppingCart()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')) // < 640
    const isTablet = useMediaQuery(theme.breakpoints.down('md')) // < 900

    const handlerIncrItemQty = () => incrItemQty(productIDNum)
    const handlerDecrItemQty = () => decrItemQty(productIDNum)

    const curStrUnit = isCAD && !isTablet ? ' CAD' : ''
    const curStr = isCAD ? ' CAD' : ''

    const item = storeItems.find((i) => i.productIDNum === productIDNum)
    if (item == null) return null
    const image = item.href
    const imagePath = config.img_path_cart

    // ─── MOBILE LAYOUT (< 640px) ───────────────────────────────────────
    if (isMobile) {
        return (
            <Paper elevation={3} sx={{ mb: 1 }}>
                <Box
                    sx={{
                        p: 1.5,
                        border: `0.5px solid ${Colors.companyColors.eb_bolt}`,
                    }}
                >
                    {/* Row 1: image + title + delete */}
                    <Box
                        display="flex"
                        alignItems="flex-start"
                        sx={{ mb: 1.5 }}
                    >
                        <Avatar
                            src={`${imagePath}${image}.jpg`}
                            sx={{
                                width: 64,
                                height: 64,
                                mr: 1.5,
                                flexShrink: 0,
                            }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    lineHeight: 1.3,
                                }}
                            >
                                <CorrectCopyright
                                    copyrightTextValue={item.descriptionTitle}
                                />
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: Colors.grayDark }}
                            >
                                {item.unitLabel}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={() => removeFromCart(item.productIDNum)}
                            sx={{ flexShrink: 0, ml: 0.5 }}
                        >
                            <DeleteForeverRoundedIcon fontSize="medium" />
                        </IconButton>
                    </Box>

                    {/* Row 2: qty control + pricing */}
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <IncDec
                            qty={quantity}
                            onIncrItemQty={handlerIncrItemQty}
                            onDecrItemQty={handlerDecrItemQty}
                        />
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography
                                variant="body2"
                                sx={{ color: Colors.grayDark }}
                            >
                                {getMoneyFormat(item.srp)} ea.
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                            >
                                {getLineItemTotal(quantity, item.srp)}
                                {curStr}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        )
    }

    // ─── TABLET / DESKTOP LAYOUT (≥ 640px) ─────────────────────────────
    return (
        <Paper elevation={3} sx={{ mb: 1 }}>
            <Box
                display="flex"
                flexDirection="row"
                sx={{
                    pt: 2,
                    pb: 2,
                    px: { sm: 1, md: 2 },
                    border: `0.5px solid ${Colors.companyColors.eb_bolt}`,
                }}
                alignItems="center"
            >
                {/* Product image */}
                <Avatar
                    src={`${imagePath}${image}.jpg`}
                    sx={{
                        width: { sm: 80, md: 128 },
                        height: { sm: 80, md: 128 },
                        mr: { sm: 2, md: 4 },
                        flexShrink: 0,
                    }}
                />

                {/* Content column */}
                <Box
                    display="flex"
                    width="100%"
                    flexDirection="column"
                    sx={{ minWidth: 0 }}
                >
                    {/* Title + delete row */}
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ pb: 1.5 }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: { sm: '1rem', md: '1.25rem' },
                                lineHeight: 1.3,
                                minWidth: 0,
                            }}
                        >
                            <CorrectCopyright
                                copyrightTextValue={item.descriptionTitle}
                            />
                        </Typography>

                        <IconButton
                            onClick={() => removeFromCart(item.productIDNum)}
                            sx={{ flexShrink: 0, ml: 1 }}
                        >
                            <DeleteForeverRoundedIcon fontSize="large" />
                        </IconButton>
                    </Box>

                    {/* Qty + pricing row */}
                    <Box>
                        <IncDec
                            qty={quantity}
                            onIncrItemQty={handlerIncrItemQty}
                            onDecrItemQty={handlerDecrItemQty}
                        />
                        <Stack
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mt: 1.0 }}
                            direction="row"
                        >
                            <Stack>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: { sm: '0.95rem', md: '110%' },
                                    }}
                                >
                                    Unit price - {getMoneyFormat(item.srp)}
                                    {curStrUnit}
                                </Typography>
                                <Typography variant="captionText">
                                    {item.unitLabel}
                                </Typography>
                            </Stack>
                            <Stack direction="column" sx={{ flexShrink: 0 }}>
                                <Typography
                                    variant="h6"
                                    align="right"
                                    sx={{
                                        fontSize: {
                                            sm: '0.95rem',
                                            md: '1.25rem',
                                        },
                                    }}
                                >
                                    Subtotal
                                </Typography>
                                <Typography
                                    variant="h6"
                                    align="right"
                                    sx={{
                                        fontSize: {
                                            sm: '0.95rem',
                                            md: '1.25rem',
                                        },
                                    }}
                                >
                                    {getLineItemTotal(quantity, item.srp)}
                                    {curStr}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Paper>
    )
}
